import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DiscordRolePicker } from '@/components/discord/DiscordPickers';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TriggerEditor, type TriggerKind } from '@/components/roles/TriggerEditor';
import { ApiError } from '@/lib/api';
import { useRoleRules, useCreateRoleRule, useDeleteRoleRule } from '@/lib/queries';
import { useCan } from '@/lib/use-can';

export function RolesListPage() {
  const rules = useRoleRules();
  const create = useCreateRoleRule();
  const remove = useDeleteRoleRule();
  const can = useCan();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [roleId, setRoleId] = useState('');
  const [triggerKind, setTriggerKind] = useState<TriggerKind>('on_join');
  const [triggerConfig, setTriggerConfig] = useState<Record<string, unknown>>({});
  const [webhookInfo, setWebhookInfo] = useState<{ url: string; secret: string } | null>(null);

  if (!can('roles.manage')) {
    return <p className="text-muted-foreground">You do not have permission to manage roles.</p>;
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    try {
      const created = await create.mutateAsync({
        name,
        triggerKind,
        triggerConfig,
        roleId,
      });
      toast.success('Rule created');
      setOpen(false);
      setName('');
      setRoleId('');
      setTriggerKind('on_join');
      setTriggerConfig({});
      if (triggerKind === 'webhook_event' && created.webhookSecret) {
        setWebhookInfo({
          url: `${window.location.origin}/api/webhooks/roles/${created.id}`,
          secret: created.webhookSecret,
        });
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  async function handleDelete(id: string, ruleName: string) {
    if (!confirm(`Delete rule "${ruleName}"?\n\nThis cannot be undone.`)) return;
    try {
      await remove.mutateAsync(id);
      toast.success('Deleted');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  const triggerLabel: Record<TriggerKind, string> = {
    on_join: 'On join',
    time_on_server: 'After N minutes',
    webhook_event: 'Webhook event',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Role rules</h1>
          <p className="text-muted-foreground">
            Automatically assign Discord roles based on triggers
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add rule
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleCreate}>
              <DialogHeader>
                <DialogTitle>New role rule</DialogTitle>
                <DialogDescription>
                  Configure the trigger and target role. You can enable/disable the rule after
                  creation.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="ruleName">Name</Label>
                  <Input
                    id="ruleName"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Auto member role"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role to grant</Label>
                  <DiscordRolePicker value={roleId} onValueChange={setRoleId} />
                </div>
                <TriggerEditor
                  kind={triggerKind}
                  config={triggerConfig}
                  onKindChange={setTriggerKind}
                  onConfigChange={setTriggerConfig}
                />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={create.isPending}>
                  Create
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Rules</CardTitle>
          <CardDescription>{rules.data?.length ?? 0} total</CardDescription>
        </CardHeader>
        <CardContent>
          {rules.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {rules.data?.length === 0 && (
            <p className="text-sm text-muted-foreground">No rules yet. Create your first rule.</p>
          )}
          {rules.data && rules.data.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Role ID</TableHead>
                  <TableHead>Enabled</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.data.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell>
                      <Link to={`/roles/${rule.id}`} className="font-medium hover:underline">
                        {rule.name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {triggerLabel[rule.triggerKind]}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {rule.roleId}
                    </TableCell>
                    <TableCell>
                      <Badge variant={rule.enabled ? 'default' : 'secondary'}>
                        {rule.enabled ? 'on' : 'off'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(rule.id, rule.name)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(webhookInfo)} onOpenChange={(o) => !o && setWebhookInfo(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Webhook ready</DialogTitle>
            <DialogDescription>
              Save the secret — it won't be shown again. You can regenerate it on the rule's detail
              page.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="space-y-1">
              <Label>URL</Label>
              <Input
                readOnly
                value={webhookInfo?.url ?? ''}
                onFocus={(e) => e.currentTarget.select()}
              />
            </div>
            <div className="space-y-1">
              <Label>Secret</Label>
              <Input
                readOnly
                value={webhookInfo?.secret ?? ''}
                onFocus={(e) => e.currentTarget.select()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setWebhookInfo(null)}>I saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
